import * as React from "react";
import type { IconComponent } from "@/components/ui/icon";

import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export interface MetricCardProps {
  /** Short metric label, e.g. "Open Tasks". */
  title: string;
  /** Primary value. Numbers are rendered as-is; pass formatted strings if needed. */
  value: React.ReactNode;
  /** Optional supporting line under the value. */
  hint?: React.ReactNode;
  /** Optional leading icon. */
  icon?: IconComponent;
  /** Show a skeleton placeholder instead of the value. */
  loading?: boolean;
  className?: string;
}

/** Compact dashboard metric tile (spec §23 Dashboard / §17 metric cards). */
export function MetricCard({
  title,
  value,
  hint,
  icon: Icon,
  loading = false,
  className,
}: MetricCardProps) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {Icon ? (
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        ) : null}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div className="text-2xl font-bold tabular-nums">{value}</div>
        )}
        {hint ? (
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default MetricCard;
